import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  CheckboxInput,
  Button,
  TYPOGRAPHY,
  Error,
  HLink,
  P,
  Input,
  Div,
} from '@figure/figure-frontlib-gui';
import { reduxPropTypes } from '@figure/frontlib-onboarding-redux';
import { useTranslation } from 'react-i18next';
import { Card } from 'shared/components/Card';
import { DisclosureReviewModal } from 'shared/components';
import { personalLoanType } from 'types/personal-loan-types';

const SoftCreditWarning = styled.p`
  ${TYPOGRAPHY.S};
  margin-bottom: 24px;
`;

export const AdditionalProfileV2 = ({
  handleSubmit,
  personalLoan,
  disclosures,
  documentUrl,
  handleDownload,
  profile,
  serverError,
}) => {
  const [activeDisclosureUuid, setActiveDisclosureUuid] = useState('');

  useEffect(() => {
    if (activeDisclosureUuid) {
      const activeDisclosure = disclosures.find((d) => d.uuid === activeDisclosureUuid);

      if (activeDisclosure && typeof handleDownload === 'function')
        handleDownload(activeDisclosure);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDisclosureUuid]);

  const [t] = useTranslation();
  const companySingleName = t('companySingleName');

  const disclosuresUrl = `/applications/${personalLoan.uuid}/signing/disclosures`;

  return (
    <Formik
      initialValues={{
        ssn: '',
        documents: [],
        antiCoercionChoice: null,
        confirmDisclosure: false,
      }}
      validationSchema={Yup.object().shape({
        documents: Yup.array().test(
          'is-not-empty',
          'Consent is required',
          (value) => value.filter((v) => !!v).length === (disclosures || []).length
        ),
        confirmDisclosure: Yup.boolean().test(
          'is-checked',
          'Consent is required',
          function (value) {
            if (this.parent.documents.length) return true;
            return value === true && !disclosures.length;
          }
        ),
        ssn: Yup.string()
          .test('has-ssn', 'Required', (value) => !!value || profile?.hasSsn === true)
          .test(
            'is-ssn',
            'Invalid',
            (value) => !value || !!value.replace(/-/g, '').match(/^$|^([0-9]{9})$/)
          ),
      })}
      onSubmit={(values) => handleSubmit(values)}
    >
      {({ values, setFieldValue, handleSubmit, errors, touched }) => (
        <Form onSubmit={handleSubmit}>
          <Card
            headerCopy={
              profile?.hasSsn
                ? 'Please read important disclosures to finalize your application.'
                : 'Provide a couple more pieces of information to finalize your application.'
            }
            customMaxWidth="840px"
          >
            {profile?.hasSsn || (
              <Input
                label="Social Security Number or Individual Taxpayer Identification Number"
                type="ssn"
                name="ssn"
                value={values.ssn}
                placeholder="xxx-xx-xxxx"
              />
            )}
            <SoftCreditWarning>
              We will pull your credit only after you complete identity and income verification.
            </SoftCreditWarning>

            {disclosures.length > 0 && (
              <>
                {disclosures.map((d, i) => (
                  <div key={d.uuid}>
                    <CheckboxInput data-test-id="confirm-read" name={`consent-${d.document.uuid}`}>
                      <P>
                        Click to confirm you have read these{' '}
                        <HLink
                          href={disclosuresUrl}
                          $css={css`
                            ${TYPOGRAPHY.M}
                          `}
                        >
                          {' '}
                          important disclosures
                        </HLink>{' '}
                        and that the information included in your application is accurate.
                      </P>
                    </CheckboxInput>

                    <DisclosureReviewModal
                      isOpen={d.uuid === activeDisclosureUuid}
                      documentUrl={d.uuid === activeDisclosureUuid ? documentUrl : ''}
                      buttonText="I accept"
                      handleClose={() => setActiveDisclosureUuid('')}
                      handleSubmit={() => {
                        setFieldValue(`documents.${i}`, d.document.uuid);
                        if (!errors.antiCoercionElection) {
                          setActiveDisclosureUuid('');
                        }
                      }}
                      descriptiveTextId="additional-profile-disclosures"
                    >
                      <P
                        className="center"
                        style={{ marginBottom: '15px' }}
                        id="additional-profile-disclosures"
                      >
                        By pressing “I accept,” I agree to be bound by the terms of the above
                        document.
                      </P>
                    </DisclosureReviewModal>
                  </div>
                ))}

                <Error display="block" id="consent-error">
                  {(touched.documents && errors.documents) || ''}
                </Error>
              </>
            )}
            {disclosures.length < 1 && (
              <>
                <CheckboxInput name="confirmDisclosure">
                  <P>
                    Click to confirm you have read these{' '}
                    <HLink href={disclosuresUrl}> important disclosures.</HLink>
                  </P>
                </CheckboxInput>
              </>
            )}
            <P
              $css={css`
                margin-bottom: 24px;
              `}
            >
              By pressing “Submit,” you authorize {companySingleName} to obtain your consumer credit
              report to evaluate your application and {companySingleName} account. We will not pull
              your credit until you complete the underwriting process. This hard credit pull may
              affect your credit score.
            </P>
            {serverError && <Error display="block">{serverError}</Error>}
            <Div
              $css={css`
                display: flex;
                justify-content: flex-end;
              `}
            >
              <Button
                type="submit"
                primary
                large
                center
                descriptiveTextId="credit-report-description"
                data-test-id="credit-report-desc-agree"
              >
                Submit
              </Button>
            </Div>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

AdditionalProfileV2.propTypes = {
  disclosures: PropTypes.arrayOf(reduxPropTypes.disclosure).isRequired,
  documentUrl: PropTypes.string,
  handleDownload: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  personalLoan: personalLoanType.isRequired,
  profile: PropTypes.shape({
    hasSsn: PropTypes.bool,
  }).isRequired,
  serverError: PropTypes.string.isRequired,
};

AdditionalProfileV2.defaultProps = {
  documentUrl: '',
};
